/**
 * WorkspaceActionBar — agent presence bar for the Queue section.
 *
 * Auto check-in on mount (no "Start Work" button). Shows: agent name, state
 * badge, elapsed timer, active order count, remaining capacity, and
 * state-appropriate action buttons.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Badge, Button, InlineStack, Text } from '@shopify/polaris'
import { engineApi } from '../../sdk/engine.api'
import type { AgentPresence, WorkflowId } from '../../sdk/engine.api'

type Props = {
  workflowId:   WorkflowId
  shop:         string
  employeeName: string
  /** Called after pull so parent can refresh the queue */
  onPulled?:    () => void
  /** Called after check-out so parent can react */
  onCheckedOut?: () => void
}

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  return [h, m, s].map(n => String(n).padStart(2, '0')).join(':')
}

const STATE_TONE: Record<string, 'success' | 'warning' | 'info' | undefined> = {
  working:  'success',
  on_break: 'warning',
  idle:     'info',
}

const STATE_LABEL: Record<string, string> = {
  working:  'Working',
  on_break: 'On Break',
  idle:     'Idle',
}

export function WorkspaceActionBar({ workflowId, shop, employeeName, onPulled, onCheckedOut }: Props) {
  const [presence,          setPresence]          = useState<AgentPresence | null>(null)
  const [activeOrders,      setActiveOrders]       = useState(0)
  const [elapsedSecs,       setElapsedSecs]        = useState(0)
  const [actionLoading,     setActionLoading]      = useState<string | null>(null)
  const [error,             setError]              = useState<string | null>(null)
  // G-9: feedback from last pull attempt (cleared on next click)
  const [pullStatus, setPullStatus] = useState<'at_capacity' | 'queue_empty' | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Auto check-in on mount: startWork = checkIn + initial pull
  useEffect(() => {
    engineApi.startWork(shop, workflowId)
      .then(res => {
        setPresence(res.presence)
        setActiveOrders(res.activeOrders)
        if (res.pulled > 0) onPulled?.()
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Check-in failed')
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop, workflowId])

  // Elapsed timer — ticks every second while presence exists
  useEffect(() => {
    if (!presence?.checkedInAt) return
    const start = new Date(presence.checkedInAt).getTime()
    timerRef.current = setInterval(() => {
      setElapsedSecs(Math.floor((Date.now() - start) / 1000))
    }, 1000)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [presence?.checkedInAt])

  const doAction = useCallback(async (label: string, fn: () => Promise<unknown>) => {
    setActionLoading(label)
    setError(null)
    try { await fn() } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed')
    } finally {
      setActionLoading(null)
    }
  }, [])

  const handlePull = () => doAction('pull', async () => {
    setPullStatus(null)
    const res = await engineApi.pull(shop, workflowId)
    const remaining = res.allowance - res.pulled
    setActiveOrders(prev => prev + res.pulled)
    if (res.allowance === 0) {
      // Backend says agent is already at capacity — no pull possible
      setPullStatus('at_capacity')
    } else if (res.pulled === 0) {
      // Had capacity but queue had no available items
      setPullStatus('queue_empty')
    } else {
      // Successfully pulled items; notify parent to refresh queue list
      onPulled?.()
      // Show capacity warning if this pull filled the agent to their limit
      if (remaining === 0) setPullStatus('at_capacity')
    }
  })

  const handleBreak = () => doAction('break', async () => {
    const p = await engineApi.startBreak(shop, workflowId)
    setPresence(p)
  })

  const handleResume = () => doAction('resume', async () => {
    const p = await engineApi.endBreak(shop, workflowId)
    setPresence(p)
  })

  const handleEndWork = () => doAction('end', async () => {
    const res = await engineApi.checkOut(shop, workflowId)
    setPresence(res.presence)
    setActiveOrders(0)
    onCheckedOut?.()
  })

  const state = presence?.state ?? 'idle'

  return (
    <div style={{
      display:      'flex',
      alignItems:   'center',
      justifyContent: 'space-between',
      gap:          12,
      padding:      '10px 16px',
      background:   '#f6f6f7',
      borderBottom: '1px solid #e1e3e5',
      flexWrap:     'wrap',
    }}>
      {/* Left: identity + info + feedback */}
      <InlineStack gap="300" blockAlign="center">
        <InlineStack gap="200" blockAlign="center">
          <Text as="span" variant="bodyMd" fontWeight="semibold">{employeeName}</Text>
          <Badge tone={STATE_TONE[state]}>{STATE_LABEL[state] ?? state}</Badge>
        </InlineStack>

        <span style={{ color: '#8c9196', fontSize: 13 }}>{formatElapsed(elapsedSecs)}</span>

        <Text as="span" variant="bodySm" tone="subdued">
          Active: <strong style={{ color: '#202223' }}>{activeOrders}</strong>
        </Text>

        {pullStatus === 'at_capacity' && (
          <Text as="span" variant="bodySm" tone="critical">وصلت للحد الأقصى</Text>
        )}
        {pullStatus === 'queue_empty' && (
          <Text as="span" variant="bodySm" tone="subdued">لا يوجد طلبات متاحة</Text>
        )}
        {error && (
          <Text as="span" variant="bodySm" tone="critical">{error}</Text>
        )}
      </InlineStack>

      {/* Right: Pull More (primary) | session controls */}
      <InlineStack gap="0" blockAlign="center">
        {state === 'working' && (
          <>
            <Button
              size="slim"
              variant="primary"
              loading={actionLoading === 'pull'}
              onClick={handlePull}
            >
              Pull More
            </Button>
            <span style={{ width: 1, height: 20, background: '#c9cccf', margin: '0 12px', display: 'inline-block' }} />
            <InlineStack gap="200" blockAlign="center">
              <Button size="slim" loading={actionLoading === 'break'} onClick={handleBreak}>Break</Button>
              <Button size="slim" tone="critical" loading={actionLoading === 'end'} onClick={handleEndWork}>End Work</Button>
            </InlineStack>
          </>
        )}
        {state === 'on_break' && (
          <InlineStack gap="200" blockAlign="center">
            <Button size="slim" variant="primary" loading={actionLoading === 'resume'} onClick={handleResume}>Resume</Button>
            <Button size="slim" tone="critical"   loading={actionLoading === 'end'}    onClick={handleEndWork}>End Work</Button>
          </InlineStack>
        )}
      </InlineStack>
    </div>
  )
}
